import { questions, SWAP_QUESTION_IDS } from "./funnelData";
import type { FunnelConceptKey } from "./funnelConcept";

// The concept-specific demand questions occupy this shared slot, so a hash pointing at
// one of them (e.g. #q-voice_demand) resolves to the same step as the base question.
const SWAPPED_SLOT_STEP = questions.findIndex((question) => question.id === "agent_pain") + 1;

export const WELCOME_STEP = 0;
export const RESULT_STEP = questions.length + 1;
export const EMAIL_STEP = RESULT_STEP + 1;

export type FunnelRouteOptions = { includeEmailStep?: boolean };

const shouldIncludeEmailStep = (options?: FunnelRouteOptions): boolean => options?.includeEmailStep ?? true;

export const getPaymentStep = (includeEmailStep = true): number => RESULT_STEP + (includeEmailStep ? 2 : 1);

export const getHashForScreen = (step: number, options?: FunnelRouteOptions): string => {
  const includeEmailStep = shouldIncludeEmailStep(options);
  if (step === WELCOME_STEP) return "#welcome";
  if (step >= 1 && step <= questions.length) return `#q-${questions[step - 1]?.id ?? "welcome"}`;
  if (step === RESULT_STEP) return "#result";
  if (includeEmailStep && step === EMAIL_STEP) return "#email";
  return "#payment";
};

export const parseHashToScreen = (
  hash: string,
  options?: FunnelRouteOptions,
): { step: number } | null => {
  const includeEmailStep = shouldIncludeEmailStep(options);
  const normalized = hash.trim().toLowerCase();
  if (!normalized || normalized === "#welcome") return { step: WELCOME_STEP };
  if (normalized === "#result") return { step: RESULT_STEP };
  if (normalized === "#email") {
    return { step: includeEmailStep ? EMAIL_STEP : getPaymentStep(false) };
  }
  if (normalized === "#payment") return { step: getPaymentStep(includeEmailStep) };
  if (normalized.startsWith("#q-")) {
    const questionId = normalized.slice(3);
    const index = questions.findIndex((question) => question.id.toLowerCase() === questionId);
    if (index !== -1) return { step: index + 1 };
    if (SWAP_QUESTION_IDS.includes(questionId) && SWAPPED_SLOT_STEP > 0) {
      return { step: SWAPPED_SLOT_STEP };
    }
  }
  return null;
};

export const getFunnelStepIndex = (step: number): number => step * 10;

export const getFunnelScreenName = (step: number, options?: FunnelRouteOptions): string => {
  const includeEmailStep = shouldIncludeEmailStep(options);
  if (step === WELCOME_STEP) return "welcome";
  if (step >= 1 && step <= questions.length) return questions[step - 1]?.id ?? "question";
  if (step === RESULT_STEP) return "result";
  if (includeEmailStep && step === EMAIL_STEP) return "email";
  return "payment";
};

export const buildFunnelPageviewPayload = (
  step: number,
  extras: { recommendedPlan: string; entryConceptKey: FunnelConceptKey; entryConceptLabel: string },
  options?: FunnelRouteOptions,
): Record<string, string | number | boolean> => {
  const hash = getHashForScreen(step, options);
  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}${window.location.pathname}${window.location.search}${hash}`
      : hash;
  const screenId = hash.startsWith("#") ? hash.slice(1) : hash;
  return {
    $current_url: url,
    funnel_hash_route: hash,
    funnel_screen_id: screenId,
    funnel_screen_name: getFunnelScreenName(step, options),
    funnel_step: step,
    funnel_step_index: getFunnelStepIndex(step),
    recommended_plan: extras.recommendedPlan,
    entry_concept: extras.entryConceptKey,
    entry_concept_label: extras.entryConceptLabel,
    product: "agentmax",
    funnel: "agentmax_webfunnel",
  };
};
